import { Button, Tour, TourProps, Tooltip } from 'antd';
import { useAtomValue } from 'jotai';
import { uiTourRefsAtom } from 'packages/omnitag/state';
import { FC, useState, useMemo, useEffect } from 'react'; // Import useEffect
import { useRecoilValue } from 'recoil';
import { uiThemeAtom } from 'packages/at-shell/state/recoil/ui';

export const WelcomeTour: FC = () => {
  const [open, setOpen] = useState(false);
  const refs = useAtomValue(uiTourRefsAtom);
  const uiTheme = useRecoilValue(uiThemeAtom);
  const [allTargetsReady, setAllTargetsReady] = useState(false); // New state to track readiness

  const steps: TourProps['steps'] = useMemo(
    () => [
      // Wrap in useMemo
      {
        title: 'Welcome to Polaris!',
        description:
          'This tour will guide you through some of the main features of Polaris.',
        placement: 'center',
      },
      {
        title: 'Your Profile Settings',
        description:
          'This is where your settings live. You can change the theme to dark mode, and sign out of Polaris here. Try toggling dark mode.',
        placement: 'bottomLeft',
        target: refs['profile-button'] ?? null,
      },
      {
        title: 'Tag Search Bar',
        description:
          'Use this dropdown to search for specific tags across our datasets. Each major category has several sub-categories to choose from. Try picking a category right now to see some results!',
        placement: 'bottom',
        target: refs['search-ui'] ?? null,
      },
      {
        title: 'Results Grid',
        description:
          "This is where your search results will appear. You can scroll through and click on any image to view it in more detail. Hovering over an image and clicking on the '+' icon will add it to your selection for data download.",
        placement: 'top',
        target: refs['results-grid'] ?? null,
      },
      {
        title: 'Modify Selection',
        description:
          'Use this dropdown to add or remove all items on the current page from your selection, or to deselect all items entirely.',
        placement: 'bottom',
        target: refs['modify-selection-dropdown'] ?? null,
      },
      {
        title: 'Filter Results',
        description:
          'Click here to open the filters panel, where you can apply various filters to narrow down your search results.',
        placement: 'bottom',
        target: refs['filter-button'] ?? null,
      },
      {
        title: 'Request Selected Data',
        description:
          'Once you have selected the data you want, click here to request a data download job for those items.',
        placement: 'bottom',
        target: refs['data-download'] ?? null,
      },
      {
        title: 'Data Requests Portal',
        description:
          'Access the Data Requests Portal from the side navigation menu to monitor the status of your data download jobs.',
        placement: 'right',
        target: refs['side-navigation-menu'] ?? null,
      },
      {
        title: 'End of Tour',
        description:
          'This concludes the Polaris welcome tour. Explore and enjoy using Polaris!',
        placement: 'center',
      },
    ],
    [refs]
  );

  // List of IDs for all steps that require a target
  const allTourTargetKeys = useMemo(
    () => [
      'profile-button',
      'search-ui',
      'results-grid',
      'modify-selection-dropdown',
      'filter-button',
      'data-download',
      'side-navigation-menu',
    ],
    []
  );

  useEffect(() => {
    const areAllTargetsPresent = allTourTargetKeys.every((key) => refs[key]);
    setAllTargetsReady(areAllTargetsPresent);
  }, [refs, allTourTargetKeys]);

  return (
    <>
      <Tooltip
        title={
          allTargetsReady
            ? 'Start a guided tour of Polaris'
            : 'Loading tour targets...'
        }
        placement="bottom"
      >
        <Button
          type="primary"
          onClick={() => setOpen(true)}
          disabled={!allTargetsReady}
        >
          Begin Tour
        </Button>
      </Tooltip>
      <Tour
        open={open}
        onClose={() => setOpen(false)}
        steps={steps}
        zIndex={1002}
        mask={
          uiTheme === 'dark'
            ? {
                color: 'rgba(255, 255, 255, 0.5)',
              }
            : true
        }
      />
    </>
  );
};
